import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import cn from "classnames"
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io"
import { Env, MultiClient } from "config"
import { AppContext } from "store/context/AppContext"
import { Tooltip } from "components/Core"
// import useLogin from "hooks/useLogin";
import icon_sidebar_helper from "helpers/icon_sidebar_helper"
import usePreRender from "hooks/usePreRender"
import useAuthorizer from "overrides/theme/entel/base/hooks/useAuthorizer"
import { get_middleware_bff } from "services/middleware_services"
import AuthContext from "store/context/PublicContext"

import sidebar_menu_info_entel from "overrides/theme/entel/base/helpers/SidebarItems.json"
import sidebar_menu_info_entel_peru from "overrides/theme/entelperu/base/helpers/SidebarItems.json"
import sidebar_menu_info_wom from "overrides/theme/wom/base/helpers/SidebarItems.json"
import sidebar_menu_info_default from "components/Core/Layout/Sidebar/SidebarItems.json"
import { CLIENT, ENV } from "config/init"

type SidebarMenuTagPayload = string[] | Record<string, unknown> | undefined

interface RemoteMenuItem {
  label?: string
  icon?: string
  url?: string
  domain?: {
    local?: string
    cloud?: string
  }
  children?: RemoteMenuItem[]
  section?: string
  enabled?: boolean
  showLegacyApp?: string
  tags?: SidebarMenuTagPayload
}

const extractRemoteMenuItems = (payload: unknown): RemoteMenuItem[] => {
  if (Array.isArray(payload)) {
    return payload as RemoteMenuItem[]
  }

  if (payload && typeof payload === "object") {
    const payloadObject = payload as Record<string, unknown>

    if (Array.isArray(payloadObject.menu)) {
      return payloadObject.menu as RemoteMenuItem[]
    }

    if (Array.isArray(payloadObject.items)) {
      return payloadObject.items as RemoteMenuItem[]
    }

    if (payloadObject.data && typeof payloadObject.data === "object") {
      const nestedData = payloadObject.data as Record<string, unknown>

      if (Array.isArray(nestedData.menu)) {
        return nestedData.menu as RemoteMenuItem[]
      }

      if (Array.isArray(nestedData.items)) {
        return nestedData.items as RemoteMenuItem[]
      }
    }
  }

  return []
}

interface SidebarItemEntry {
  id?: string
  name: string
  type: "normal" | "collapsible"
  icon?: string
  href?: string
  position: "top" | "bottom"
  isBeta?: boolean
  items?: Array<{ name: string; href: string }>
}

const SIMO_V4_LEGACY_APP_ID = "simoV4"
const SIMO_V4_REQUIRED_TAG = "beta"
const SIDEBAR_SECTIONS = new Set(["sidebar-menu", "sidebar-setting"])

const normalizeTagValues = (tags: SidebarMenuTagPayload): string[] => {
  if (!tags) return []

  if (Array.isArray(tags)) {
    return tags
      .filter((tag): tag is string => typeof tag === "string")
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean)
  }

  if (typeof tags === "object") {
    return Object.entries(tags)
      .filter(([, value]) => value === true)
      .map(([key]) => key.trim().toLowerCase())
      .filter(Boolean)
  }

  return []
}

const isEligibleForSimoV4 = (item: RemoteMenuItem): boolean => {
  if (item.enabled !== true) return false
  if (item.showLegacyApp !== SIMO_V4_LEGACY_APP_ID) return false

  const tags = normalizeTagValues(item.tags)
  return tags.includes(SIMO_V4_REQUIRED_TAG)
}

const decorateCrossDomainUrl = (resolvedUrl: string): string => {
  try {
    const targetUrl = new URL(resolvedUrl)
    const currentLocation = window.location

    const isCrossDomain =
      targetUrl.hostname !== currentLocation.hostname || (targetUrl.port || "") !== (currentLocation.port || "")

    if (isCrossDomain) {
      const targetPath = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}` || "/"
      targetUrl.searchParams.set("returnTo", targetPath)
    }

    return targetUrl.toString()
  } catch {
    return resolvedUrl
  }
}

const resolveRemoteHref = (item: RemoteMenuItem, client: string, env: string): string | undefined => {
  const rawUrl = item.url?.trim()
  if (!rawUrl) return undefined

  if (/^https?:\/\//i.test(rawUrl)) {
    return decorateCrossDomainUrl(rawUrl)
  }

  const isLocalHost = window.location.hostname.includes("localhost")
  const baseDomain = isLocalHost ? item.domain?.local?.trim() : resolveRemoteHrefCustomClient(item, client, env)

  if (!baseDomain) {
    return rawUrl
  }

  try {
    return decorateCrossDomainUrl(new URL(rawUrl, baseDomain).toString())
  } catch {
    return rawUrl
  }
}

const resolveRemoteHrefCustomClient = (item: RemoteMenuItem, client: string, env: string) => {
  if (client === CLIENT.ENTEL_PERU && env !== ENV.PROD) {
    return item.domain?.cloud?.replace("{{client}}", CLIENT.ENTEL_PERU).replace("{{env}}", `-${env}`).trim()
  }
  if (client === CLIENT.ENTEL_PERU && env === ENV.PROD) {
    return item.domain?.cloud?.replace("{{client}}", "entelpe").replace("{{env}}", "").trim()
  }
  const envToken = env === ENV.PROD ? "" : env
  return item.domain?.cloud?.replace("{{client}}", client).replace("{{env}}", envToken).trim()
}

const toSidebarLeafEntry = (
  item: RemoteMenuItem,
  client: string,
  env: string,
  inheritedSection?: string,
): SidebarItemEntry | null => {
  const name = item.label?.trim()
  if (!name) return null
  if (!isEligibleForSimoV4(item)) return null

  const href = resolveRemoteHref(item, client, env)
  if (!href) return null

  const resolvedSection = item.section || inheritedSection
  if (resolvedSection && !SIDEBAR_SECTIONS.has(resolvedSection)) {
    return null
  }

  return {
    name,
    type: "normal",
    icon: item.icon || "business",
    href,
    isBeta: true,
    position: resolvedSection === "sidebar-setting" ? "bottom" : "top",
  }
}

const flattenRemoteEntries = (
  items: RemoteMenuItem[],
  client: string,
  env: string,
  inheritedSection?: string,
): SidebarItemEntry[] =>
  items.flatMap((item) => {
    const resolvedSection = item.section || inheritedSection

    if (Array.isArray(item.children) && item.children.length > 0) {
      return flattenRemoteEntries(item.children, client, env, resolvedSection)
    }

    const leafEntry = toSidebarLeafEntry(item, client, env, inheritedSection)
    return leafEntry ? [leafEntry] : []
  })

const mapRemoteMenuToSidebar = (menuItems: RemoteMenuItem[], client: string, env: string): SidebarItemEntry[] =>
  flattenRemoteEntries(menuItems, client, env)

const getSidebarItemIdentity = (item: SidebarItemEntry): string => {
  if (item.type === "normal") {
    return `normal:${item.href || item.name}`
  }

  return `collapsible:${item.id || item.name}`
}

const SidebarItems = ({ sidebar_opened }: any) => {
  let [_client, _env] = MultiClient.get_user_env(null, Env.clients, Env.env, Env.defaults)
  const { _color } = usePreRender()
  const { set_open_sidebar } = useContext(AppContext)
  const authContext = useContext(AuthContext)
  const token = authContext?.user?.token as string | undefined
  const { logout_user_action } = useAuthorizer()
  const selected_path = useLocation()

  const sidebar_menu_info =
    _client === CLIENT.WOM
      ? sidebar_menu_info_wom
      : _client === CLIENT.ENTEL
        ? sidebar_menu_info_entel
        : _client === CLIENT.ENTEL_PERU
          ? sidebar_menu_info_entel_peru
          : sidebar_menu_info_default

  const [remoteSidebarItems, setRemoteSidebarItems] = useState<SidebarItemEntry[]>([])

  useEffect(() => {
    let cancelled = false

    const loadSidebarConfig = async () => {
      if (!token) {
        if (!cancelled) {
          setRemoteSidebarItems([])
        }
        return
      }

      try {
        const candidateEndpoints = ["/config/menu", "/api/config/menu"]
        let menuItems: RemoteMenuItem[] = []

        for (let index = 0; index < candidateEndpoints.length; index += 1) {
          const endpoint = candidateEndpoints[index]
          const isLastEndpoint = index === candidateEndpoints.length - 1

          try {
            const response = await get_middleware_bff<unknown>(endpoint, token)
            const payload = response?.data?.message ?? response?.data
            const extractedItems = extractRemoteMenuItems(payload)

            if (extractedItems.length > 0 || isLastEndpoint) {
              menuItems = extractedItems
              break
            }
          } catch (error: any) {
            const status = error?.response?.status
            if (status !== 404 || isLastEndpoint) {
              throw error
            }
          }
        }

        const mappedItems = mapRemoteMenuToSidebar(menuItems, _client, _env)

        if (!cancelled) {
          setRemoteSidebarItems(mappedItems)
        }
      } catch {
        if (!cancelled) {
          setRemoteSidebarItems([])
        }
      }
    }

    loadSidebarConfig()

    return () => {
      cancelled = true
    }
  }, [token, _client, _env])

  const sidebarMenuEntries = useMemo<SidebarItemEntry[]>(() => {
    const staticEntries = (sidebar_menu_info as { data?: SidebarItemEntry[] })?.data || []

    if (remoteSidebarItems.length === 0) return staticEntries

    // Group all remote items under a single collapsible "New" entry.
    // The beta badge lives on the parent — children render without it.
    const newGroupEntry: SidebarItemEntry = {
      id: "xmonitor-new",
      name: "Nuevo",
      type: "collapsible",
      icon: "Sparkles",
      isBeta: true,
      position: "top",
      items: remoteSidebarItems.filter((e) => Boolean(e.href)).map((e) => ({ name: e.name, href: e.href! })),
    }

    // Avoid duplicating if somehow already present
    const existingIds = new Set(staticEntries.map(getSidebarItemIdentity))
    if (existingIds.has(getSidebarItemIdentity(newGroupEntry))) return staticEntries

    return [...staticEntries, newGroupEntry]
  }, [remoteSidebarItems, sidebar_menu_info])

  // Estado para almacenar los menús colapsables abiertos
  const [openMenus, setOpenMenus] = useState<string[]>([])

  // Leer el estado inicial desde localStorage
  useEffect(() => {
    const savedState = localStorage.getItem("openMenus")
    if (savedState) {
      setOpenMenus(JSON.parse(savedState))
    }
  }, [])

  // Guardar el estado en localStorage al cambiar
  useEffect(() => {
    localStorage.setItem("openMenus", JSON.stringify(openMenus))
  }, [openMenus])

  // Respond to auth token requests from child xMonitor windows opened via cross-domain links.
  // xMonitor cannot access simo-v4's localStorage directly (different origins), so it requests
  // the Auth0 cache via postMessage and bootstraps its own localStorage from the response.
  useEffect(() => {
    const handleAuthRequest = (event: MessageEvent) => {
      if (event.data?.type !== "OMNIX_AUTH_REQUEST" || !event.source) return
      const cache: Record<string, string> = {}
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key?.startsWith("@@auth0spajs@@")) {
          cache[key] = localStorage.getItem(key) ?? ""
        }
      }
      ;(event.source as Window).postMessage({ type: "OMNIX_AUTH_CACHE", cache }, "*")
    }
    window.addEventListener("message", handleAuthRequest)
    return () => window.removeEventListener("message", handleAuthRequest)
  }, [])

  const handle_open_sidebar = useCallback(() => set_open_sidebar(true), [set_open_sidebar])

  const items_top = useMemo(
    () =>
      sidebarMenuEntries.filter((item: SidebarItemEntry) => {
        if (item.position !== "top") return false
        if (_env === ENV.PROD && item.id === "pre-releases") return false
        return true
      }) || [],
    [sidebarMenuEntries, _env],
  )
  const items_bottom = useMemo(
    () =>
      sidebarMenuEntries.filter((item: SidebarItemEntry) => {
        if (item.position !== "bottom") return false
        if (_env === ENV.PROD && item.id === "pre-releases") return false
        return true
      }) || [],
    [sidebarMenuEntries, _env],
  )

  const ItemCollapsible = ({ name, icon, items, id, isBeta }: any) => {
    const isOpen = openMenus.includes(id)
    const Icon = icon_sidebar_helper(icon)

    const handle_collapse = useCallback(() => {
      setOpenMenus((prev) => (isOpen ? prev.filter((menuId) => menuId !== id) : [...prev, id]))
    }, [isOpen, id])

    return (
      <div className="w-full">
        <div
          onClick={handle_collapse}
          className={cn(
            "text-md w-full h-12 flex flex-row items-center justify-between px-6 cursor-pointer hover:bg-[#F0F0F0]",
            `hover:text_${_color}`,
            {
              "font-bold": items.some((item: any) => item.href === selected_path?.pathname),
              "bg-[#F0F0F0]": items.some((item: any) => item.href === selected_path?.pathname),
            },
          )}
        >
          <div className="flex flex-row items-center justify-start gap-4">
            <Icon className="text-lg" />
            <p className={cn(`hover:text_${_color}`)}>{name}</p>
            {isBeta ? (
              <span className="rounded-md bg-blue-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                beta
              </span>
            ) : null}
          </div>
          {isOpen ? <IoIosArrowUp className="text-lg" /> : <IoIosArrowDown className="text-lg" />}
        </div>
        {isOpen ? (
          <div className="flex flex-col">
            {items.map((_it: any, _i: number) => (
              <ItemClickeable name={_it?.name} href={_it?.href} from_collapsible key={_i} />
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  const ItemClickeable = ({ icon, name, href, from_collapsible, isBeta }: any) => {
    const Icon = icon_sidebar_helper(icon)
    const handle_logout = useCallback(() => icon === "logout" && logout_user_action(), [icon])
    const isExternalHref = typeof href === "string" && /^https?:\/\//i.test(href)

    // Open cross-domain links via window.open() — NOT via <a rel="noopener"> because
    // noopener/noreferrer nullify window.opener in the child tab, breaking the postMessage
    // Auth0 cache handoff that restores the session without a second login.
    const handleExternalClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault()
        handle_logout()
        window.open(href, "_blank")
      },
      [href, handle_logout],
    )

    const itemClasses = cn(
      "w-full h-12 flex flex-row items-center justify-start gap-4 px-2 pl-6 hover:bg-[#F0F0F0]",
      `hover:text_${_color}`,
      {
        "pl-8": Boolean(from_collapsible),
        "font-bold": Boolean(href === selected_path?.pathname),
        "text-black": Boolean(href !== selected_path?.pathname),
        "bg-[#F0F0F0]": Boolean(href === selected_path?.pathname),
      },
    )

    const content = (
      <>
        {Icon ? from_collapsible ? null : <Icon className="text-lg" /> : null}
        <p className="text-md">{name}</p>
        {isBeta ? (
          <span className="ml-auto rounded-md bg-blue-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            beta
          </span>
        ) : null}
      </>
    )

    const compactContent = (
      <>
        {Icon ? <Icon className="text-lg" /> : null}
        {isBeta ? (
          <span className="ml-1 rounded-md bg-blue-500 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">B</span>
        ) : null}
      </>
    )

    if (!sidebar_opened) {
      return (
        <div className="w-full flex flex-row items-center justify-center py-2">
          <Tooltip content={name} align="right">
            {isExternalHref ? (
              <a onClick={handleExternalClick} href={href} className="flex items-center justify-center cursor-pointer">
                {compactContent}
              </a>
            ) : (
              <Link
                onClick={handle_logout}
                to={href || "#"}
                className="flex items-center justify-center cursor-pointer"
              >
                {compactContent}
              </Link>
            )}
          </Tooltip>
        </div>
      )
    }

    if (isExternalHref) {
      return (
        <a onClick={handleExternalClick} href={href} className={itemClasses}>
          {content}
        </a>
      )
    }

    return (
      <Link onClick={handle_logout} to={href || "#"} className={itemClasses}>
        {content}
      </Link>
    )
  }

  const IconShrink = ({ item: { icon, name } }: any) => {
    const Icon = icon_sidebar_helper(icon)

    return (
      <div className="w-full flex flex-row items-center justify-center py-2">
        <Tooltip content={name} align="right">
          <Icon onClick={handle_open_sidebar} className={cn("text-xl cursor-pointer", `hover:text_${_color}`)} />
        </Tooltip>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col items-center justify-between overflow-auto">
      <div
        className={cn("w-full flex-col", {
          "lg:hidden": Boolean(!sidebar_opened),
          flex: Boolean(sidebar_opened),
        })}
      >
        {items_top.map((item: any, i: number) => {
          if (item.type === "normal")
            return (
              <ItemClickeable name={item?.name} href={item?.href} icon={item?.icon} isBeta={item?.isBeta} key={i} />
            )
          if (item.type === "collapsible")
            return (
              <ItemCollapsible
                id={item?.id}
                name={item?.name}
                icon={item?.icon}
                items={item?.items}
                isBeta={item?.isBeta}
                key={i}
              />
            )
          return null
        })}
      </div>
      <div
        className={cn("w-full flex flex-col", {
          "lg:hidden": Boolean(!sidebar_opened),
        })}
      >
        {items_bottom.map((item, i) => {
          return <ItemClickeable name={item?.name} href={item?.href} icon={item?.icon} isBeta={item?.isBeta} key={i} />
        })}
      </div>

      <div
        className={cn("w-full flex-col overflow-auto hidden", {
          hidden: Boolean(sidebar_opened),
          "lg:flex": Boolean(!sidebar_opened),
        })}
      >
        {items_top.map((item, i) => {
          return <IconShrink key={i} item={item} className="text-lg" />
        })}
      </div>
      <div
        className={cn("w-full flex-col hidden", {
          hidden: Boolean(sidebar_opened),
          "lg:flex": Boolean(!sidebar_opened),
        })}
      >
        {items_bottom.map((item, i) => {
          return <IconShrink key={i} item={item} className="text-lg" />
        })}
      </div>
    </div>
  )
}

export default SidebarItems
